"use client";

import Link from "next/link";
import { FocusEvent, KeyboardEvent, useId, useRef, useState } from "react";

import { linkMatchingMap } from "@/components/layout/navigationData";
import { getTourChromeStyles } from "@/components/layout/tourChromeStyles";
import { cn } from "@/lib/utils/cn";

import { NavbarDropdownGroupProps } from "@/lib/types/nav";

import { IoMdArrowDropdown } from "react-icons/io";

export default function NavbarDropdownGroup({
  label,
  items,
  isGhostPage,
  pathName,
  tourTheme,
}: NavbarDropdownGroupProps) {
  const chrome = getTourChromeStyles(tourTheme, isGhostPage).header;
  const childLinkActive = linkMatchingMap[label]?.includes(pathName);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const skipFocusOpenRef = useRef(false);
  const baseId = useId();
  const buttonId = `${baseId}-button`;
  const menuId = `${baseId}-menu`;

  // FIX BUG WITH HAUNTED BLOGS HIGHLIGHTING THE WRONG DROPDOWN LINK
  const handleBlur = (event: FocusEvent<HTMLLIElement>) => {
    const nextTarget = event.relatedTarget;

    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) {
      return;
    }

    setIsOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      skipFocusOpenRef.current = true;
      setIsOpen(false);
      buttonRef.current?.focus();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      window.requestAnimationFrame(() => {
        menuRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
      });
    }
  };

  const handleMouseLeave = () => {
    const activeElement = document.activeElement;

    if (
      activeElement instanceof Node &&
      containerRef.current?.contains(activeElement)
    ) {
      return;
    }

    setIsOpen(false);
  };

  const handleFocus = (event: FocusEvent<HTMLLIElement>) => {
    if (skipFocusOpenRef.current) {
      skipFocusOpenRef.current = false;
      return;
    }

    const target = event.target;

    if (target instanceof Node && buttonRef.current?.contains(target)) {
      return;
    }

    setIsOpen(true);
  };

  const handleTriggerClick = () => {
    setIsOpen((current) => !current);
  };

  return (
    <li
      ref={containerRef}
      className="relative z-10"
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={buttonRef}
        type="button"
        id={buttonId}
        aria-controls={menuId}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={handleTriggerClick}
        className={cn(
          chrome.navLink,
          childLinkActive || isOpen ? chrome.navActive : "",
          "flex cursor-pointer items-center gap-1 rounded-sm text-lg font-semibold uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4",
        )}
      >
        {label}
        <IoMdArrowDropdown
          aria-hidden="true"
          size={15}
          className={cn("transition-transform", isOpen && "rotate-180")}
        />
      </button>

      <ul
        ref={menuRef}
        id={menuId}
        aria-labelledby={buttonId}
        className={cn(
          "absolute left-[-1rem] top-7 z-50 m-0 list-none whitespace-nowrap p-0 pt-5",
          isOpen ? "flex flex-col" : "hidden",
        )}
      >
        {items.map((item) => (
          <li key={item.href}>
            <Link
              href={item.href}
              onClick={() => setIsOpen(false)}
              className={cn(
                chrome.dropdownItem,
                item.href === pathName ? chrome.dropdownActive : "",
                "flex items-center gap-1 border-b px-4 py-3 text-[15px] font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]",
              )}
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
}
